import React, { useState, useMemo, useRef, useEffect } from 'react';
import { DebounceInput } from 'react-debounce-input';
import humanizeSlug from '../utils/humanizeSlug';

const buildSearchIndex = (sections, drafts = []) => {
  const index = [];
  sections.forEach((section) => {
    section.chapters.forEach((chapter) => {
      chapter.articles.forEach((article) => {
        const key = `${section.slug}/${chapter.slug}/${article.slug}`;
        index.push({
          key,
          title: article.title,
          sectionTitle: section.title,
          chapterTitle: chapter.title,
          titleText: (article.title || '').toLowerCase(),
          tagsText: (article.tags || []).join(' ').toLowerCase(),
          bodyText: (article.body || '').toLowerCase(),
        });
      });
    });
  });
  drafts.forEach((draft) => {
    const title = humanizeSlug(draft.slug);
    index.push({
      key: `draft:${draft.slug}`,
      slug: draft.slug,
      draftState: draft.state,
      title,
      sectionTitle: humanizeSlug(draft.section),
      chapterTitle: humanizeSlug(draft.chapter),
      titleText: `${draft.slug} ${title} ${draft.section} ${draft.chapter}`.toLowerCase(),
      tagsText: '',
      bodyText: '',
    });
  });
  return index;
};

const DRAFT_BADGES = { draft: 'Draft', revision: 'Pending revision' };

const TITLE_WEIGHT = 5;
const TAG_WEIGHT = 3;
const BODY_WEIGHT = 1;

const termScore = (item, term) => {
  if (item.titleText.includes(term)) return TITLE_WEIGHT;
  if (item.tagsText.includes(term)) return TAG_WEIGHT;
  if (item.bodyText.includes(term)) return BODY_WEIGHT;
  return 0;
};

export const searchManual = (searchIndex, query) => {
  const terms = query.toLowerCase().trim().split(/\s+/);
  return searchIndex
    .map((item) => ({ item, scores: terms.map((term) => termScore(item, term)) }))
    .filter(({ scores }) => scores.every((score) => score > 0))
    .map(({ item, scores }) => ({ item, score: scores.reduce((sum, score) => sum + score, 0) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 10)
    .map(({ item }) => item);
};

export { buildSearchIndex };

const UserManualSearch = ({ sections, onSelectArticle, drafts = [], onSelectDraft }) => {
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchIndex = useMemo(() => buildSearchIndex(sections, drafts), [sections, drafts]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const results = useMemo(() => {
    if (!query || query.trim().length < 2) return [];
    return searchManual(searchIndex, query);
  }, [query, searchIndex]);

  return (
    <div className="user-manual-search" ref={containerRef}>
      <svg
        className="user-manual-search-icon"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
          clipRule="evenodd"
        />
      </svg>
      <DebounceInput
        type="search"
        debounceTimeout={300}
        placeholder="Search documentation..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {query.trim().length >= 2 && (
        <div className="user-manual-search-results">
          {results.length > 0 ? (
            <ul className="list-unstyled">
              {results.map((result) => (
                <li key={result.key}>
                  <a
                    href={`#${result.key}`}
                    className="user-manual-search-result"
                    onClick={(e) => {
                      e.preventDefault();
                      setQuery('');
                      if (result.draftState) {
                        onSelectDraft(result.slug, result.draftState);
                      } else {
                        onSelectArticle(result.key);
                      }
                    }}
                  >
                    <span className="user-manual-search-result-title">
                      {result.title}
                      {result.draftState && (
                        <span className="user-manual-search-result-badge">{DRAFT_BADGES[result.draftState]}</span>
                      )}
                    </span>
                    <span className="user-manual-search-result-path">
                      {result.sectionTitle} &rsaquo; {result.chapterTitle}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted setlist-text-sm px-2 py-1">No results found.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManualSearch;
